import React from 'react';
import { ActivityIndicator, Platform, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import type { AvailableIconName } from '@/assets/icons';
import { Scroll, Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianCircleStatusBadge, {
  getBadgeStatus,
} from '@/features/guardian-player/components/guardian-circle/guardian-circle-status-badge';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import { GuardianCircleMemberStatus } from '@/features/guardian-player/types';
import { createStyles, useScaling } from '@/hooks';
import { PlayerGuardianStatus } from '@/services/get-player-guardians';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.invitationSent;

/** Invite statuses map onto the shared circle badge (same Waiting/Expired/Declined pill). */
const CIRCLE_STATUS_BY_INVITE: Record<PlayerGuardianStatus, GuardianCircleMemberStatus> = {
  [PlayerGuardianStatus.Active]: GuardianCircleMemberStatus.Joined,
  [PlayerGuardianStatus.Waiting]: GuardianCircleMemberStatus.Waiting,
  [PlayerGuardianStatus.Expired]: GuardianCircleMemberStatus.Expired,
  [PlayerGuardianStatus.Declined]: GuardianCircleMemberStatus.Declined,
};

export type InvitationSentContentProps = {
  screenTitle: string;
  /** Email (just-sent) or the member's display name (from the circle). */
  contact: string;
  canJoinLabel: string;
  status: PlayerGuardianStatus;
  expiresLabel: string;
  relationshipLabel: string;
  backLabel: string;
  onBack: () => void;
  onBackToCircle: () => void;
  onRevoke: () => void;
  onShareLink: () => void;
  onResend: () => void;
  onRemove: () => void;
  isShareLoading?: boolean;
};

type ActionVariant = 'primary' | 'outline' | 'danger' | 'tertiary';

export default function InvitationSentContent({
  screenTitle,
  contact,
  canJoinLabel,
  status,
  expiresLabel,
  relationshipLabel,
  onBack,
  onRevoke,
  onShareLink,
  onResend,
  onRemove,
  isShareLoading,
}: InvitationSentContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const { bottom } = useSafeAreaInsets();

  const badgeStatus = getBadgeStatus(CIRCLE_STATUS_BY_INVITE[status]);

  const renderActions = () => {
    switch (status) {
      case PlayerGuardianStatus.Expired:
        return (
          <Stack gap={scale(12)}>
            <ActionButton variant="primary" label={strings.resend} onPress={onResend} />
            <ActionButton variant="outline" label={strings.remove} onPress={onRemove} />
          </Stack>
        );
      case PlayerGuardianStatus.Declined:
        return (
          <ActionButton variant="outline" label={strings.removeInvitation} onPress={onRemove} />
        );
      case PlayerGuardianStatus.Waiting:
        return (
          <>
            <ActionButton
              variant="danger"
              icon="IcXClose"
              iconSize={20}
              label={strings.revoke}
              onPress={onRevoke}
            />
            <ActionButton
              variant="primary"
              icon="IcShare"
              label={strings.shareLink}
              onPress={onShareLink}
              isLoading={isShareLoading}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Stack
      viewType="gradient"
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.root}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack}
        titleAlign="center"
        titleVariant="Font Guardian App/Body/Body 16 Bold"
        containerProps={!isTablet ? { backgroundColor: 'transparent' } : undefined}
      />

      <Scroll
        flex={1}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.content,
          // Sits just clear of the home indicator / nav bar — enough not to look glued to the
          // edge, not so much that the actions float away from the bottom.
          { paddingBottom: bottom + scale(12) },
        ]}>
        {/* `flexGrow` + no shrink, deliberately NOT `flex: 1`. Growing lets the body fill a tall
            screen so `styles.actions`' auto margin can push the buttons to the bottom; refusing
            to shrink lets it exceed the viewport on a short screen so the ScrollView actually
            scrolls. `flex: 1` implies `flexShrink: 1`, which is what compressed the actions
            under the nav bar with nothing to scroll. */}
        <Stack
          flexGrow={1}
          flexShrink={0}
          width="100%"
          maxWidth={isTablet ? scale(380) : undefined}
          alignSelf="center"
          gap={scale(24)}>
          {/* Hero: envelope in a circle with a success check badge. */}
          <Stack align="center" gap={scale(12)}>
            <Stack style={styles.heroWrap}>
              <Stack align="center" justify="center" style={styles.heroCircle}>
                <SvgIcon name="IcSmsBold" size={scale(54)} color={colors['0']} />
              </Stack>
              <Stack align="center" justify="center" style={styles.heroCheck}>
                <SvgIcon name="IcCheckMd" size={scale(22)} color={colors['0']} strokeWidth={6} />
              </Stack>
            </Stack>

            <Stack align="center" gap={scale(8)}>
              <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.contact}>
                {contact}
              </Text>
              <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.canJoin}>
                {canJoinLabel}
              </Text>
            </Stack>
          </Stack>

          {/* Co-guardian sharing card */}
          <Stack style={styles.card} gap={scale(10)}>
            <Stack direction="row" align="center" gap={scale(6)}>
              <SvgIcon name="IcListCheck" size={scale(16)} color={guardianColors['Main/Main']} />
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 11 Semibold"
                style={styles.cardTitle}>
                {strings.sharingTitle}
              </Text>
            </Stack>

            <InfoRow label={strings.statusLabel}>
              {badgeStatus ? <GuardianCircleStatusBadge status={badgeStatus} /> : null}
            </InfoRow>
            <InfoRow label={strings.expiresLabel} value={expiresLabel}>
              {isShareLoading ? (
                <ActivityIndicator size="small" color={guardianColors['10']} />
              ) : undefined}
            </InfoRow>
            <InfoRow label={strings.relationshipLabel} value={relationshipLabel} />
          </Stack>

          {status === PlayerGuardianStatus.Waiting ? (
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={styles.waitingHint}>
              {strings.waitingHint}
            </Text>
          ) : null}

          {/* Case-specific actions + back link. `marginTop: auto` keeps them at the bottom
              when there is room to spare, and collapses to nothing when there isn't — unlike a
              `flex: 1` spacer, which stops the content growing past the viewport. */}
          <Stack gap={scale(12)} style={styles.actions}>
            {renderActions()}
          </Stack>
        </Stack>
      </Scroll>
    </Stack>
  );
}

/** One label/value line in the sharing card; `value` or a `children` node (e.g. the badge). */
function InfoRow({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  const styles = useStyles();
  return (
    <Stack direction="row" align="center" justify="space-between">
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.rowLabel}>
        {label}
      </Text>
      {children ?? (
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.rowValue}>
          {value}
        </Text>
      )}
    </Stack>
  );
}

const ActionButton = React.memo(function ActionButton({
  label,
  onPress,
  variant,
  icon,
  iconSize = 18,
  flex,
  isLoading,
}: {
  label: string;
  onPress: () => void;
  variant: ActionVariant;
  icon?: AvailableIconName;
  /** Icon size in px (pre-scale). Defaults to 18. */
  iconSize?: number;
  flex?: boolean;
  isLoading?: boolean;
}) {
  const styles = useStyles();
  const { scale } = useScaling();

  const {
    container: containerStyle,
    label: labelStyle,
    iconColor,
  } = {
    primary: {
      container: styles.buttonPrimary,
      label: styles.labelPrimary,
      iconColor: colors['0'],
    },
    outline: {
      container: styles.buttonOutline,
      label: styles.labelOutline,
      iconColor: guardianColors['Main/Main'],
    },
    danger: {
      container: styles.buttonDanger,
      label: styles.labelDanger,
      iconColor: guardianColors.Error,
    },
    tertiary: {
      container: styles.buttonTertiary,
      label: styles.labelTertiary,
      iconColor: guardianColors['Main/Main'],
    },
  }[variant];

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={isLoading ? undefined : onPress}
      disabled={isLoading}
      style={({ pressed }) => [
        styles.button,
        containerStyle,
        flex ? styles.buttonFlex : null,
        pressed || isLoading ? styles.pressed : null,
      ]}>
      {isLoading ? (
        <ActivityIndicator color={iconColor} size="small" />
      ) : icon ? (
        <SvgIcon name={icon} size={scale(iconSize)} color={iconColor} />
      ) : null}
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={labelStyle}>
        {label}
      </Text>
    </Pressable>
  );
});

const HERO_SIZE = 160;
const HERO_CHECK_SIZE = 40;

const useStyles = createStyles(({ scale, isTablet }) => ({
  root: {
    flex: 1,
  },
  actions: {
    marginTop: 'auto',
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: scale(20),
    paddingTop: scale(40),
    paddingBottom: scale(24),
    ...(Platform.OS === 'web' || isTablet
      ? {
          width: '100%',
          maxWidth: 380,
          alignSelf: 'center',
        }
      : {}),
  },
  heroWrap: {
    width: scale(HERO_SIZE),
    height: scale(HERO_SIZE),
  },
  heroCircle: {
    width: scale(HERO_SIZE),
    height: scale(HERO_SIZE),
    borderRadius: scale(HERO_SIZE / 2),
    backgroundColor: guardianColors['Main/Main'],
    borderWidth: scale(6),
    borderColor: colors['0'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: scale(4) },
    shadowOpacity: 0.1,
    shadowRadius: scale(12),
    elevation: 4,
  },
  heroCheck: {
    position: 'absolute',
    right: scale(6),
    bottom: scale(6),
    width: scale(HERO_CHECK_SIZE),
    height: scale(HERO_CHECK_SIZE),
    borderRadius: scale(HERO_CHECK_SIZE / 2),
    backgroundColor: colors.Success,
    borderWidth: scale(1.6),
    borderColor: colors['0'],
  },
  contact: {
    color: guardianColors['Main/Main'],
  },
  canJoin: {
    color: guardianColors['6'],
  },
  card: {
    width: '100%',
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
  },
  cardTitle: {
    color: guardianColors['6'],
  },
  rowLabel: {
    color: guardianColors['10'],
  },
  rowValue: {
    color: guardianColors['10'],
  },
  waitingHint: {
    color: guardianColors['6'],
    textAlign: 'center',
    paddingHorizontal: scale(8),
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    height: scale(48),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(16),
    paddingVertical: scale(10),
  },
  buttonFlex: {
    flex: 1,
  },
  buttonPrimary: {
    backgroundColor: guardianColors['Main/Main'],
    shadowColor: 'rgba(41,39,87,0.2)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 1,
    elevation: 2,
  },
  buttonOutline: {
    backgroundColor: colors['0'],
    borderWidth: 0.5,
    borderColor: guardianColors['Main/Main'],
    shadowColor: 'rgba(0,0,0,0.2)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 1,
    elevation: 2,
  },
  buttonDanger: {
    backgroundColor: colors['0'],
    borderWidth: 1,
    borderColor: guardianColors.Error,
  },
  buttonTertiary: {
    backgroundColor: '#E6EDFC',
  },
  labelPrimary: {
    color: colors['0'],
  },
  labelOutline: {
    color: guardianColors['Main/Main'],
  },
  labelDanger: {
    color: guardianColors.Error,
  },
  labelTertiary: {
    color: guardianColors['Main/Main'],
  },
  backLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(6),
    paddingVertical: scale(8),
  },
  backLinkText: {
    color: guardianColors['Main/Main'],
  },
  pressed: {
    opacity: 0.7,
  },
}));
